'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  FileSpreadsheet,
  Users,
  Smartphone,
  Apple,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileDown,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  ChevronRight,
  Database
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

interface ParsedContact {
  name: string;
  phone: string;
  email?: string;
  notes?: string;
}

interface ServiceOption {
  id: string;
  name: string;
  duration: number;
  cashPrice: number;
}

export default function ImportSettingsPage() {
  // Estado para wizard de contactos
  const [contactsModalOpen, setContactsModalOpen] = useState<boolean>(false);
  const [contactStep, setContactStep] = useState<number>(1);
  const [contactSource, setContactSource] = useState<'ANDROID' | 'IPHONE' | 'GOOGLE' | 'EXCEL'>('EXCEL');
  const [contactText, setContactText] = useState<string>('');
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [importingContacts, setImportingContacts] = useState<boolean>(false);
  const [contactsResult, setContactsResult] = useState<{ imported: number; skipped: number } | null>(null);

  // Estado para importador de turnos
  const [appointmentsModalOpen, setAppointmentsModalOpen] = useState<boolean>(false);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [appointmentsText, setAppointmentsText] = useState<string>('');
  const [importingAppointments, setImportingAppointments] = useState<boolean>(false);
  const [appointmentsResult, setAppointmentsResult] = useState<{ imported: number; batchId: string } | null>(null);

  // Cargar servicios disponibles para plantilla
  useEffect(() => {
    fetch('/api/services')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.services)) {
          setServices(data.services);
          setSelectedServices(data.services.map((s: any) => s.id));
        }
      })
      .catch((e) => console.warn('Error loading services for template:', e));
  }, []);

  // Parsear texto o archivo CSV / VCF de contactos
  const handleParseContacts = (rawText: string) => {
    const lines = rawText.split(/\r?\n/);
    const list: ParsedContact[] = [];

    // Parseo básico de vCard
    if (rawText.includes('BEGIN:VCARD')) {
      let currentName = '';
      let currentPhone = '';
      let currentEmail = '';

      for (const line of lines) {
        if (line.startsWith('FN:')) {
          currentName = line.replace('FN:', '').trim();
        } else if (line.startsWith('TEL')) {
          const parts = line.split(':');
          if (parts[1]) currentPhone = parts[1].trim();
        } else if (line.startsWith('EMAIL')) {
          const parts = line.split(':');
          if (parts[1]) currentEmail = parts[1].trim();
        } else if (line.startsWith('END:VCARD')) {
          if (currentName && currentPhone) {
            list.push({ name: currentName, phone: currentPhone, email: currentEmail });
          }
          currentName = '';
          currentPhone = '';
          currentEmail = '';
        }
      }
    } else {
      // Parseo de CSV o lista plana (Nombre, Teléfono)
      for (const line of lines) {
        if (!line.trim()) continue;
        const cols = line.split(/[,;\t]/).map((c) => c.trim().replace(/^["']|["']$/g, ''));
        if (cols.length >= 2) {
          const col0 = cols[0];
          const col1 = cols[1];
          // Detectar si col0 o col1 es teléfono
          const col0HasLetters = /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(col0);
          const name = col0HasLetters ? col0 : col1;
          const phone = col0HasLetters ? col1 : col0;
          const email = cols[2] && cols[2].includes('@') ? cols[2] : undefined;

          if (name && phone) {
            list.push({ name, phone, email });
          }
        }
      }
    }

    setParsedContacts(list);
    setContactStep(3);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        setContactText(content);
        handleParseContacts(content);
      }
    };
    reader.readAsText(file);
  };

  const submitContactsImport = async () => {
    if (parsedContacts.length === 0) return;
    setImportingContacts(true);
    try {
      const res = await fetch('/api/import/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacts: parsedContacts,
          source: contactSource
        })
      });
      const data = await res.json();
      if (data.success) {
        setContactsResult({
          imported: data.importedCount,
          skipped: data.skippedCount
        });
        setContactStep(4);
      } else {
        alert(data.error || 'Error al importar');
      }
    } catch (e: any) {
      alert(e.message || 'Error de conexión');
    } finally {
      setImportingContacts(false);
    }
  };

  // Descargar plantilla CSV para turnos
  const downloadTemplateCsv = () => {
    const activeServs = services.filter((s) => selectedServices.includes(s.id));
    const header = 'Cliente,Telefono,Servicio,Fecha(AAAA-MM-DD),Hora(HH:MM),Profesional,Precio\n';
    const sampleRows = activeServs.map((s, idx) => {
      const date = new Date(Date.now() - (idx + 1) * 86400000).toISOString().split('T')[0];
      return `Cliente Ejemplo ${idx + 1},112345678${idx},${s.name},${date},14:00,Juan Rojas,${s.cashPrice}`;
    }).join('\n');

    const blob = new Blob([header + sampleRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'plantilla_turnos_historicos.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Enviar importación de turnos
  const submitAppointmentsImport = async () => {
    if (!appointmentsText.trim()) return;
    setImportingAppointments(true);
    try {
      const lines = appointmentsText.trim().split(/\r?\n/);
      const rows = [];
      for (const line of lines) {
        if (!line.trim() || line.toLowerCase().startsWith('cliente')) continue;
        const [clientName, clientPhone, serviceName, dateStr, time, staffName, price] = line
          .split(/[,;\t]/)
          .map((c) => c.trim().replace(/^["']|["']$/g, ''));
        if (clientName) {
          rows.push({ clientName, clientPhone, serviceName, dateStr, time, staffName, price });
        }
      }

      const res = await fetch('/api/import/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows })
      });
      const data = await res.json();
      if (data.success) {
        setAppointmentsResult({
          imported: data.importedCount,
          batchId: data.batchId
        });
      } else {
        alert(data.error || 'Error al importar turnos');
      }
    } catch (e: any) {
      alert(e.message || 'Error de red');
    } finally {
      setImportingAppointments(false);
    }
  };

  // Descargar exportación de clientes
  const downloadClientsCsv = async () => {
    try {
      const res = await fetch('/api/clients');
      const data = await res.json();
      if (data.success && Array.isArray(data.clients)) {
        const header = 'Nombre,Telefono,Email,DNI,Puntos,TotalGastado,CantidadTurnos,Creado\n';
        const rows = data.clients.map((c: any) =>
          `"${c.name}","${c.phone}","${c.email || ''}","${c.dni || ''}",${c.points || 0},${c.totalSpent || 0},${c.appointmentsCount || 0},"${c.createdAt || ''}"`
        ).join('\n');

        const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `clientes_appgenda_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      alert('Error descargando clientes');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard/settings"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver a Configuración</span>
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
            Importar histórico
          </h1>
          <p className="text-sm text-slate-500">
            Migrá tus clientes y turnos pasados desde WhatsApp, contactos del teléfono, Google o archivos Excel/CSV sin perder datos.
          </p>
        </div>
      </div>

      {/* Grid con Sidebar Unificado */}
      <div className="grid grid-cols-12 gap-6 items-start">
        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <SettingsSidebar currentPath="/dashboard/settings/import" />
        </div>

        {/* Contenido Principal */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          {/* 2 Opciones Principales de Migración */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Opción 1: Traer Contactos */}
            <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-all space-y-5">
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">Traer tus contactos</h2>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Subí tus contactos de WhatsApp, iPhone, Android o Google para que tengan su ficha armada y te agenden más rápido.
                  </p>
                </div>

                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                    📱 vCard (.vcf)
                  </span>
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                    📋 Google Contacts
                  </span>
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                    📊 Excel / CSV
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setContactStep(1);
                  setParsedContacts([]);
                  setContactsResult(null);
                  setContactsModalOpen(true);
                }}
                className="w-full py-3 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Comenzar importación de contactos</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Opción 2: Cargar Excel con Histórico */}
            <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-all space-y-5">
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">Cargar un Excel con histórico</h2>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Te generamos una plantilla Excel adaptada a tus servicios y profesionales para cargar turnos anteriores sin desorden.
                  </p>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3 text-xs text-slate-600 space-y-1">
                  <span className="font-bold text-slate-800 block text-[11px]">Control anti-errores:</span>
                  <p className="text-[11px] leading-relaxed">
                    Comprueba que los profesionales y servicios coincidan exactamente para evitar duplicados en la base de datos.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setAppointmentsResult(null);
                  setAppointmentsModalOpen(true);
                }}
                className="w-full py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Generar plantilla e importar turnos</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Bloque de Exportaciones */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider text-[11px] text-slate-400">
                Exportar Datos de tu Negocio
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Descargá copias de seguridad de tus registros en formato CSV estándar compatible con Excel.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div className="p-4 rounded-2xl border border-slate-200/90 bg-slate-50/50 flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <h3 className="text-xs font-bold text-slate-900">Directorio de Clientes</h3>
                  <p className="text-[11px] text-slate-500">Nombres, teléfonos, historial de gasto y puntos.</p>
                </div>
                <button
                  type="button"
                  onClick={downloadClientsCsv}
                  className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-100 transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0"
                >
                  <FileDown className="w-3.5 h-3.5 text-emerald-600" />
                  <span>CSV</span>
                </button>
              </div>

              <div className="p-4 rounded-2xl border border-slate-200/90 bg-slate-50/50 flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <h3 className="text-xs font-bold text-slate-900">Reporte de Ventas y Caja</h3>
                  <p className="text-[11px] text-slate-500">Historial transaccional y desglose contable.</p>
                </div>
                <Link
                  href="/dashboard/sales"
                  className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-100 transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
                  <span>Ir a Ventas</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL 1: WIZARD DE CONTACTOS (4 PASOS) */}
      {contactsModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-7 shadow-2xl space-y-5 border border-slate-200">
            {/* Header del Modal */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Importador de Contactos</h3>
                <span className="text-xs text-slate-400">Paso {contactStep} de 4</span>
              </div>
              <button
                type="button"
                onClick={() => setContactsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs"
              >
                ✕
              </button>
            </div>

            {/* PASO 1: DE DÓNDE SALEN */}
            {contactStep === 1 && (
              <div className="space-y-4">
                <p className="text-xs text-slate-600">
                  ¿Desde qué aplicación o formato querés exportar tus contactos?
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setContactSource('ANDROID')}
                    className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                      contactSource === 'ANDROID'
                        ? 'border-emerald-600 bg-emerald-50/50 shadow-2xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Smartphone className="w-5 h-5 text-emerald-700 mb-1.5" />
                    <span className="text-xs font-bold text-slate-900 block">Celular Android</span>
                    <span className="text-[11px] text-slate-500">Contactos de Google o Samsung (.vcf)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setContactSource('IPHONE')}
                    className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                      contactSource === 'IPHONE'
                        ? 'border-emerald-600 bg-emerald-50/50 shadow-2xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Apple className="w-5 h-5 text-slate-800 mb-1.5" />
                    <span className="text-xs font-bold text-slate-900 block">iPhone / iCloud</span>
                    <span className="text-[11px] text-slate-500">Exportar vCard desde iCloud (.vcf)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setContactSource('GOOGLE')}
                    className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                      contactSource === 'GOOGLE'
                        ? 'border-emerald-600 bg-emerald-50/50 shadow-2xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Users className="w-5 h-5 text-blue-600 mb-1.5" />
                    <span className="text-xs font-bold text-slate-900 block">Google Contacts</span>
                    <span className="text-[11px] text-slate-500">Archivo CSV descargado de Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setContactSource('EXCEL')}
                    className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                      contactSource === 'EXCEL'
                        ? 'border-emerald-600 bg-emerald-50/50 shadow-2xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <FileSpreadsheet className="w-5 h-5 text-teal-600 mb-1.5" />
                    <span className="text-xs font-bold text-slate-900 block">Excel / CSV plano</span>
                    <span className="text-[11px] text-slate-500">Columnas de Nombre y Teléfono</span>
                  </button>
                </div>

                <div className="pt-2 text-right">
                  <button
                    type="button"
                    onClick={() => setContactStep(2)}
                    className="px-5 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold cursor-pointer transition-all"
                  >
                    Continuar a Subir
                  </button>
                </div>
              </div>
            )}

            {/* PASO 2: SUBIR ARCHIVO O PEGAR */}
            {contactStep === 2 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-3xl p-6 text-center space-y-3 bg-slate-50/50">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-800">Arrastrá tu archivo o seleccionalo</p>
                    <p className="text-[11px] text-slate-400">Soporta .vcf (vCard), .csv, .txt</p>
                  </div>
                  <input
                    type="file"
                    accept=".vcf,.csv,.txt"
                    onChange={handleFileUpload}
                    className="text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">O pegá texto directo (Nombre y Teléfono):</label>
                  <textarea
                    rows={4}
                    value={contactText}
                    onChange={(e) => setContactText(e.target.value)}
                    placeholder="Ejemplo:&#10;Juan Perez, 1155443322&#10;María Gomez, 3415556677"
                    className="w-full text-xs font-mono p-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-emerald-600"
                  />
                </div>

                <div className="flex justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => setContactStep(1)}
                    className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900"
                  >
                    Atrás
                  </button>
                  <button
                    type="button"
                    disabled={!contactText.trim()}
                    onClick={() => handleParseContacts(contactText)}
                    className="px-5 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold cursor-pointer disabled:opacity-50"
                  >
                    Procesar Contactos
                  </button>
                </div>
              </div>
            )}

            {/* PASO 3: REVISAR Y DEDUPLICAR */}
            {contactStep === 3 && (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between text-xs text-emerald-900">
                  <div>
                    <span className="font-bold text-emerald-950 block text-sm">
                      {parsedContacts.length} contactos detectados
                    </span>
                    <span>Se normalizarán los prefijos argentinos (+54 9...) y se omitirán duplicados existentes.</span>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                </div>

                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-2xl bg-slate-50/50">
                  {parsedContacts.slice(0, 15).map((c, i) => (
                    <div key={i} className="px-3.5 py-2 flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-800">{c.name}</span>
                      <span className="font-mono text-slate-500">{c.phone}</span>
                    </div>
                  ))}
                  {parsedContacts.length > 15 && (
                    <div className="p-2 text-center text-[11px] text-slate-400 font-medium">
                      ...y {parsedContacts.length - 15} contactos más
                    </div>
                  )}
                </div>

                <div className="flex justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => setContactStep(2)}
                    className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900"
                  >
                    Atrás
                  </button>
                  <button
                    type="button"
                    disabled={importingContacts || parsedContacts.length === 0}
                    onClick={submitContactsImport}
                    className="px-5 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold cursor-pointer disabled:opacity-50 flex items-center gap-2"
                  >
                    {importingContacts && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    <span>Confirmar e Importar</span>
                  </button>
                </div>
              </div>
            )}

            {/* PASO 4: LISTO */}
            {contactStep === 4 && contactsResult && (
              <div className="space-y-5 text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-slate-900">¡Importación completada!</h4>
                  <p className="text-xs text-slate-500">
                    Se crearon <strong className="text-emerald-800 font-bold">{contactsResult.imported}</strong> clientes nuevos en tu base de datos de PostgreSQL.
                  </p>
                  {contactsResult.skipped > 0 && (
                    <p className="text-[11px] text-slate-400">
                      ({contactsResult.skipped} duplicados o con formato inválido fueron omitidos).
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setContactsModalOpen(false)}
                  className="px-6 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold"
                >
                  Cerrar Asistente
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 2: IMPORTADOR DE TURNOS HISTÓRICOS */}
      {appointmentsModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-7 shadow-2xl space-y-5 border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Importar Histórico de Turnos</h3>
                <span className="text-xs text-slate-400">Generá tu plantilla personalizada o subí los datos</span>
              </div>
              <button
                type="button"
                onClick={() => setAppointmentsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs"
              >
                ✕
              </button>
            </div>

            {appointmentsResult ? (
              <div className="space-y-5 text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-slate-900">¡Turnos cargados exitosamente!</h4>
                  <p className="text-xs text-slate-500">
                    Se crearon <strong className="text-emerald-800 font-bold">{appointmentsResult.imported}</strong> turnos históricos con estado completado.
                  </p>
                  <span className="text-[10px] text-slate-400 font-mono block">Lote: {appointmentsResult.batchId}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setAppointmentsModalOpen(false)}
                  className="px-6 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold"
                >
                  Finalizar
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 block">
                    1. Elegí qué servicios querés incluir en la plantilla:
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-36 overflow-y-auto p-2 border border-slate-200 rounded-2xl bg-slate-50">
                    {services.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(s.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedServices([...selectedServices, s.id]);
                            } else {
                              setSelectedServices(selectedServices.filter((id) => id !== s.id));
                            }
                          }}
                          className="rounded text-emerald-700 focus:ring-emerald-600"
                        />
                        <span className="truncate">{s.name}</span>
                      </label>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={downloadTemplateCsv}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 hover:underline pt-1 cursor-pointer"
                  >
                    <FileDown className="w-4 h-4 text-emerald-600" />
                    <span>Descargar plantilla CSV con ejemplos</span>
                  </button>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 block">
                    2. Pegá las filas del CSV o archivo de turnos:
                  </label>
                  <textarea
                    rows={4}
                    value={appointmentsText}
                    onChange={(e) => setAppointmentsText(e.target.value)}
                    placeholder="Cliente,Telefono,Servicio,Fecha,Hora,Profesional,Precio&#10;Lucía V.,1122334455,Corte tradicional,2026-09-10,15:00,Juan Rojas,12000"
                    className="w-full text-xs font-mono p-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-emerald-600"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    disabled={importingAppointments || !appointmentsText.trim()}
                    onClick={submitAppointmentsImport}
                    className="px-5 py-2.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold cursor-pointer disabled:opacity-50 flex items-center gap-2"
                  >
                    {importingAppointments && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    <span>Procesar e Importar Turnos</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
